<script lang="ts" setup>
import { MoreOptionsVerticalIcon } from "@frontiers/brink-icons";
import { OnClickOutside } from "@vueuse/components";
import ToolbarNavItem from "./ToolbarNavItem.vue";
import type {
  IToolbarNavItemProps,
  IToolbarNavProps
} from "./ToolbarNav.models";

const props = withDefaults(defineProps<IToolbarNavProps>(), {
  items: () => new Array<IToolbarNavItemProps>(),
  mobileItems: () => new Array<IToolbarNavItemProps>()
});

const isResizingBody = inject("isResizingBody", ref(false));
const listEl = ref<HTMLElement | null>(null);
const isMoreOpen = ref(false);
const hiddenCount = ref(0);

const primaryItem = computed(() => props.items[0]);

const pinnedItems = computed(() =>
  props.items.slice(1).filter((item) => item.items?.length)
);

const overflowableItems = computed<IToolbarNavItemProps[]>(() => [
  ...props.mobileItems,
  ...props.items.slice(1).filter((item) => !item.items?.length)
]);

const visibleItems = computed(() => {
  const end = overflowableItems.value.length - hiddenCount.value;
  return overflowableItems.value.slice(0, end);
});

const overflowItems = computed(() => {
  if (hiddenCount.value === 0) return [];
  return overflowableItems.value.slice(
    overflowableItems.value.length - hiddenCount.value
  );
});

const showMoreButton = computed(() => overflowItems.value.length > 0);

const checkOverflow = () => {
  if (!listEl.value) return;
  if (hiddenCount.value >= overflowableItems.value.length) return;

  if (listEl.value.scrollWidth > listEl.value.clientWidth) {
    hiddenCount.value++;
    setTimeout(checkOverflow, 0);
  }
};

const recalculate = () => {
  hiddenCount.value = 0;
  nextTick(() => checkOverflow());
};

const toggleMore = () => {
  isMoreOpen.value = !isMoreOpen.value;
};

const closeMore = () => {
  isMoreOpen.value = false;
};

const onOverflowItemClick = (item: IToolbarNavItemProps) => {
  if (item.onClick) item.onClick();
  else if (item.url) window.location.href = item.url;
  closeMore();
};

watch(isResizingBody, recalculate);

onMounted(recalculate);
</script>
<template>
  <nav class="ToolbarNav">
    <!-- Desktop -->
    <ul class="ToolbarNav__list ToolbarNav__list--desktop">
      <ToolbarNavItem
        v-for="(item, index) in items"
        :key="item.label"
        v-bind="item"
        :class="{ 'ToolbarNavItem--primary': index === 0 }" />
    </ul>

    <!-- Mobile -->
    <ul
      ref="listEl"
      class="ToolbarNav__list ToolbarNav__list--mobile">
      <ToolbarNavItem
        v-for="item in visibleItems"
        :key="`m-${item.label}`"
        v-bind="item" />

      <ToolbarNavItem
        v-for="item in pinnedItems"
        :key="`pinned-${item.label}`"
        v-bind="item" />

      <ToolbarNavItem
        v-if="primaryItem"
        :key="`primary-${primaryItem.label}`"
        v-bind="primaryItem"
        class="ToolbarNavItem--primary" />

      <li
        v-if="showMoreButton"
        class="ToolbarNavItem ToolbarNavItem--more">
        <OnClickOutside @trigger="closeMore">
          <button
            type="button"
            class="ToolbarNavItem__link"
            :aria-expanded="isMoreOpen"
            aria-haspopup="menu"
            @click="toggleMore">
            <span class="ToolbarNavItem__icon">
              <MoreOptionsVerticalIcon size="lg" />
            </span>
            <p class="ToolbarNavItem__text">More</p>
          </button>
          <ul
            role="menu"
            class="ToolbarNavItem__menu"
            :class="{ 'ToolbarNavItem__menu--open': isMoreOpen }">
            <li
              v-for="item in overflowItems"
              :key="`overflow-${item.label}`"
              class="ToolbarNavItem__menu__item">
              <button
                class="ToolbarNavItem__menu__button"
                @click="onOverflowItemClick(item)">
                <span class="ToolbarNavItem__menu__text">{{ item.label }}</span>
              </button>
            </li>
          </ul>
        </OnClickOutside>
      </li>
    </ul>
  </nav>
</template>
<style lang="scss">
.ToolbarNav {
  width: 96px;
  position: sticky;
  opacity: 1;
  pointer-events: auto;
  top: calc(var(--ibar-mix-height) + spacing(6));
  margin-top: 0;
  transition:
    opacity 0.28s $bezier,
    margin-top 0.28s $bezier;
  padding-bottom: 48px;

  @media #{$break-md} {
    position: fixed;
    bottom: 0;
    right: 0;
    top: auto;
    padding-bottom: 16px;
    width: 100vw;
    display: grid;
    place-items: center;
    z-index: 12;
  }

  &__list {
    padding: 8px;
    display: flex;
    gap: 8px;
    background: $white;
    border: 1px solid $grey40;
    border-radius: spacing(1);

    &--desktop {
      flex-direction: column;

      @media #{$break-md} {
        display: none;
      }
    }

    &--mobile {
      display: none;
      flex-direction: row;
      justify-content: space-between;
      box-shadow: $elevation-bottom-10;

      @media #{$break-md} {
        display: flex;
        max-width: calc(100vw - 32px);
      }

      @media #{$break-xs} {
        max-width: calc(100vw - 16px);
      }
    }
  }
}
</style>
